/**
 * ContextMenuManager - Handles document context menu display and actions
 *
 * Extracted from Sidebar.ts to reduce coupling and improve maintainability.
 * The context menu provides: favorite, rename, knowledge toggle, folder move,
 * workspace move, archive, and delete actions.
 */

import type { DocumentMeta, Folder, Workspace } from '../services/api';

export interface ContextMenuCallbacks {
  onFavorite: (docId: string, isFavorite: boolean) => void;
  onRename: (docId: string, newTitle: string) => void;
  onKnowledge: (docId: string, enabled: boolean) => void;
  onMove: (docId: string, folderId: string | null) => void;
  onWorkspace: (docId: string, workspaceId: string) => void;
  onArchive: (docId: string, isArchived: boolean) => void;
  onDelete: (docId: string) => void;
}

export class ContextMenuManager {
  private callbacks: Partial<ContextMenuCallbacks> = {};
  private folders: Folder[] = [];
  private workspaces: Workspace[] = [];

  /**
   * Update available folders for the "Move to" menu
   */
  setFolders(folders: Folder[]): void {
    this.folders = folders;
  }

  /**
   * Update available workspaces for the workspace menu
   */
  setWorkspaces(workspaces: Workspace[]): void {
    this.workspaces = workspaces;
  }

  /**
   * Set callback for a specific action
   */
  setCallback<K extends keyof ContextMenuCallbacks>(
    action: K,
    callback: ContextMenuCallbacks[K]
  ): void {
    this.callbacks[action] = callback;
  }

  /**
   * Show context menu for a document
   */
  show(doc: DocumentMeta, x: number, y: number): void {
    // Remove existing menu
    const existingMenu = document.querySelector('.context-menu');
    if (existingMenu) {
      existingMenu.remove();
    }

    const menu = document.createElement('div');
    menu.className = 'context-menu';
    menu.style.left = `${x}px`;
    menu.style.top = `${y}px`;

    const hasKnowledge = doc.isKnowledgeEnabled === true;

    // Option: Favorite
    this.addMenuItem(menu, {
      text: doc.isFavorite ? '☆ Quitar de favoritos' : '⭐ Favorito',
      onClick: () => {
        this.callbacks.onFavorite?.(doc.id, !doc.isFavorite);
        menu.remove();
      }
    });

    // Option: Rename
    this.addMenuItem(menu, {
      text: '✏️ Renombrar',
      onClick: () => {
        const newTitle = prompt('Nuevo nombre:', doc.title);
        if (newTitle && newTitle.trim() && newTitle !== doc.title) {
          this.callbacks.onRename?.(doc.id, newTitle.trim());
        }
        menu.remove();
      }
    });

    // Separator
    this.addSeparator(menu);

    // Option: Knowledge
    this.addMenuItem(menu, {
      html: `<span>🧠 Knowledge Base</span><span class="context-menu-check">${hasKnowledge ? '✓' : ''}</span>`,
      className: 'has-check',
      onClick: () => {
        this.callbacks.onKnowledge?.(doc.id, !hasKnowledge);
        menu.remove();
      }
    });

    // Separator
    this.addSeparator(menu);

    // Header: Move to folder
    this.addHeader(menu, 'Mover a:');

    // Option: No folder
    const docFolderId = (doc.customFields as Record<string, unknown>)?.folderId as string | null | undefined;
    this.addMenuItem(menu, {
      text: '📄 Sin carpeta',
      className: !docFolderId ? 'active' : '',
      onClick: () => {
        if (docFolderId) {
          this.callbacks.onMove?.(doc.id, null);
        }
        menu.remove();
      }
    });

    // Folder options
    for (const folder of this.folders) {
      this.addMenuItem(menu, {
        text: `📁 ${folder.name}`,
        className: docFolderId === folder.id ? 'active' : '',
        onClick: () => {
          if (docFolderId !== folder.id) {
            this.callbacks.onMove?.(doc.id, folder.id);
          }
          menu.remove();
        }
      });
    }

    // Workspace options (if more than one)
    if (this.workspaces.length > 1) {
      this.addSeparator(menu);
      this.addHeader(menu, 'Workspace:');

      for (const ws of this.workspaces) {
        this.addMenuItem(menu, {
          text: `📋 ${ws.name}`,
          className: doc.workspaceId === ws.id ? 'active' : '',
          onClick: () => {
            if (doc.workspaceId !== ws.id) {
              this.callbacks.onWorkspace?.(doc.id, ws.id);
            }
            menu.remove();
          }
        });
      }
    }

    // Separator
    this.addSeparator(menu);

    // Option: Archive/Unarchive
    this.addMenuItem(menu, {
      text: doc.isArchived ? '📤 Desarchivar' : '📦 Archivar',
      onClick: () => {
        this.callbacks.onArchive?.(doc.id, !doc.isArchived);
        menu.remove();
      }
    });

    // Option: Delete
    this.addMenuItem(menu, {
      text: '🗑️ Eliminar',
      style: 'color: var(--error)',
      onClick: () => {
        this.callbacks.onDelete?.(doc.id);
        menu.remove();
      }
    });

    document.body.appendChild(menu);

    // Close on click outside
    const closeHandler = (e: MouseEvent) => {
      if (!menu.contains(e.target as Node)) {
        menu.remove();
        document.removeEventListener('click', closeHandler);
      }
    };
    setTimeout(() => document.addEventListener('click', closeHandler), 0);

    // Adjust position if off-screen
    this.adjustPosition(menu);
  }

  private addMenuItem(
    menu: HTMLElement,
    options: {
      text?: string;
      html?: string;
      className?: string;
      style?: string;
      onClick: () => void;
    }
  ): void {
    const item = document.createElement('div');
    item.className = `context-menu-item${options.className ? ` ${options.className}` : ''}`;
    if (options.style) {
      item.style.cssText = options.style;
    }
    if (options.html) {
      item.innerHTML = options.html;
    } else if (options.text) {
      item.textContent = options.text;
    }
    item.addEventListener('click', options.onClick);
    menu.appendChild(item);
  }

  private addSeparator(menu: HTMLElement): void {
    const sep = document.createElement('div');
    sep.className = 'context-menu-separator';
    menu.appendChild(sep);
  }

  private addHeader(menu: HTMLElement, text: string): void {
    const header = document.createElement('div');
    header.className = 'context-menu-header';
    header.textContent = text;
    menu.appendChild(header);
  }

  private adjustPosition(menu: HTMLElement): void {
    const rect = menu.getBoundingClientRect();
    if (rect.right > window.innerWidth) {
      menu.style.left = `${window.innerWidth - rect.width - 10}px`;
    }
    if (rect.bottom > window.innerHeight) {
      menu.style.top = `${window.innerHeight - rect.height - 10}px`;
    }
  }
}
